import { Box, Flex, Icon } from "@chakra-ui/react";
import { BuildingsIcon } from "@phosphor-icons/react/dist/ssr";
import { AdvancedMarker } from "@vis.gl/react-google-maps";

// Compact "$1.4M" / "$189K" style. Marker real estate is tight — full
// "$1,234,567" would either truncate or force the pill very wide on the
// map. Using compact en-US notation; the $ prefix reads as MXN in
// context (consistent with the result-card pricing throughout /Buscar).
const compactPriceFormatter = new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
});
const formatPriceCompact = (n) => `$${compactPriceFormatter.format(n)}`;

// Zillow-style price-text marker. Two variants:
//   property → dark pill, price only (e.g. "$1.2M")
//   development → same dark pill, BuildingsIcon glyph leading + "+" suffix
//     on the price (e.g. "🏢 $1.2M+"). Icon = kind-of-thing; "+" = price
//     floor (starting from). Distinct signals so devs read differently
//     at-a-glance from individual properties.
// If `price` is missing or unparseable, falls back to a small generic dot
// for both variants — better than dropping the marker entirely (the
// listing is still mappable and clickable).
const PriceMarker = ({
    position,
    price,
    variant = "property",
    isHovered = false,
    isSelected = false,
    onClick,
    // Forwarded to the underlying AdvancedMarker so MarkersLayer can hand
    // the native marker element to MarkerClusterer.
    markerRef,
    // Initial-load gate — held opaque until MarkersLayer finishes its first
    // clusterer sync. Without this, every individual price pill paints
    // before MarkerClusterer hides the ones in clusters, producing a
    // maximalist flash. After first sync the gate is permanently open;
    // subsequent prop changes don't re-hide.
    hidden = false,
    // Bidirectional hover sync — wired to the pill DOM (not the
    // AdvancedMarker root, which doesn't support pointer events directly).
    onPointerEnter,
    onPointerLeave,
    // Forwarded to AdvancedMarker so MarkersLayer can lift the hovered
    // marker above its neighbors when it would otherwise be occluded.
    zIndex,
    // When true, the pill paints but doesn't intercept pointer events. Used
    // by HoveredMarkerOverlay — that parallel marker sits on top of the real
    // one, so without this the real marker's pointerLeave fires the instant
    // the overlay covers it, kicking off a hover bounce (hoveredId → null →
    // hoveredId → null …) that reads as the pill stuttering between scaled
    // and unscaled. With nonInteractive=true the mouse passes through to the
    // real marker, which keeps holding the hover.
    nonInteractive = false,
    // Number of listings bucketed at this exact coord. When > 1, the pill
    // gets a `+N` badge to signal "this is a stack — click for the list".
    // 1 (or undefined) renders as a normal single-listing pill.
    stackCount = 1,
}) => {
    const numericPrice = parseFloat(price);
    const hasPrice = Number.isFinite(numericPrice) && numericPrice > 0;
    const isDevelopment = variant === "development";

    // Dev markers wear Guia yellow (#FFCD00, brand.500) with navy fg;
    // property markers wear Guia navy (#002B49, accent.500) with white fg.
    // Hover state swaps to an explicit lighter shade per variant —
    // CSS `filter: brightness(1.1)` on saturated yellow was very visible
    // but on navy was nearly imperceptible, so the hover signal felt
    // inconsistent. Picking the lifted colors directly keeps both variants
    // brand-coherent (navy stays navy, yellow stays yellow) AND visibly
    // brighter on hover.
    const fgColor = isDevelopment ? "#002B49" : "white";
    const isLifted = isHovered || isSelected;
    const fillColor = isDevelopment
        ? isLifted
            ? "#FFE066"
            : "#FFCD00"
        : isLifted
          ? "#1A4A6E"
          : "#002B49";

    const hoverScale = isLifted ? 1.25 : 1;
    // Base drop shadow only. The white border below carries the
    // overlap-disambiguation job that the halo used to.
    const ringShadow = "0 2px 8px rgba(0,0,0,0.25)";

    if (!hasPrice) {
        return (
            <AdvancedMarker
                ref={markerRef}
                position={position}
                onClick={nonInteractive ? undefined : onClick}
                zIndex={zIndex}
            >
                <Box
                    w="14px"
                    h="14px"
                    bg={fillColor}
                    borderRadius="full"
                    borderWidth="2px"
                    borderColor="white"
                    boxShadow={ringShadow}
                    transform={`scale(${hoverScale})`}
                    transition="transform 0.12s ease, background-color 0.12s ease, opacity 0.12s ease, box-shadow 0.12s ease"
                    cursor={nonInteractive ? "default" : "pointer"}
                    opacity={hidden ? 0 : 1}
                    pointerEvents={hidden || nonInteractive ? "none" : "auto"}
                    onPointerEnter={nonInteractive ? undefined : onPointerEnter}
                    onPointerLeave={nonInteractive ? undefined : onPointerLeave}
                />
            </AdvancedMarker>
        );
    }

    return (
        <AdvancedMarker
            ref={markerRef}
            position={position}
            onClick={nonInteractive ? undefined : onClick}
            zIndex={zIndex}
        >
            <Flex
                bg={fillColor}
                color={fgColor}
                px={2.5}
                h="26px"
                align="center"
                gap={1}
                borderRadius="full"
                borderWidth="2px"
                borderColor="white"
                borderStyle="solid"
                fontSize="xs"
                fontWeight={700}
                whiteSpace="nowrap"
                boxShadow={ringShadow}
                transform={`scale(${hoverScale})`}
                transition="transform 0.12s ease, background-color 0.12s ease, opacity 0.12s ease, box-shadow 0.12s ease"
                cursor={nonInteractive ? "default" : "pointer"}
                opacity={hidden ? 0 : 1}
                pointerEvents={hidden || nonInteractive ? "none" : "auto"}
                onPointerEnter={nonInteractive ? undefined : onPointerEnter}
                onPointerLeave={nonInteractive ? undefined : onPointerLeave}
            >
                {isDevelopment && (
                    <Icon boxSize={3.5}>
                        <BuildingsIcon weight="fill" />
                    </Icon>
                )}
                {formatPriceCompact(numericPrice)}
                {isDevelopment && "+"}
                {stackCount > 1 && (
                    <Box
                        as="span"
                        bg="whiteAlpha.300"
                        color={fgColor}
                        fontSize="2xs"
                        fontWeight={700}
                        rounded="full"
                        px={1.5}
                        ml={1}
                        lineHeight="1.4"
                    >
                        +{stackCount - 1}
                    </Box>
                )}
            </Flex>
        </AdvancedMarker>
    );
};

export default PriceMarker;
